import React, { memo, ReactNode, useState } from "react";

import { Link01 } from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/General";
import { Menu, UnstyledButton } from "@mantine/core";
import { useNavigate } from "react-router-dom";

import { ReactComponent as Archive } from "assets/icons/archive.svg";
import { ReactComponent as Duplicate } from "assets/icons/copy-fill.svg";
import { ReactComponent as PencilIcon } from "assets/icons/manage.svg";
import { BlockchainEntity } from "components/BlockchainEntityTitle/types";
import { envConfig, getMintPageUrl, TOOLS_URL } from "config";
import { SubMenuItem } from "~/features/collections/sections/ContractCollectionDashboard/menu/BlockchainEntityMenu";
import CollectionArchiveModal from "~/features/collections/sections/ContractCollectionDashboard/menu/CollectionArchiveModal";
import CollectionDuplicateModal from "~/features/collections/sections/ContractCollectionDashboard/menu/CollectionDuplicateModal";

import { useArchiveCollection } from "./collectionArchiveHook";

const CollectionMenu = ({
  entity,
  menuButton,
}: {
  entity: BlockchainEntity;
  menuButton: ReactNode;
}) => {
  const navigate = useNavigate();
  const [archiveCollectionModalOpened, setArchiveCollectionModalOpened] =
    useState(false);
  const [duplicateCollectionModalOpened, setDuplicateCollectionModalOpened] =
    useState(false);
  const { toggleArchive } = useArchiveCollection({
    entity,
  });

  const items: SubMenuItem[] = [
    {
      label: "Manage",
      icon: <PencilIcon />,
      onClick: () =>
        navigate(
          TOOLS_URL.collections.manage.replace(":collectionId", entity.id)
        ),
    },
    ...(!entity.isArchive
      ? [
          {
            label: "Duplicate",
            icon: <Duplicate />,
            onClick: () => {
              setDuplicateCollectionModalOpened(true);
            },
          },
        ]
      : []),
    {
      label: entity.isArchive ? "Unarchive" : "Archive",
      icon: <Archive />,
      onClick: () => {
        if (entity.isArchive) {
          return toggleArchive();
        }
        setArchiveCollectionModalOpened(true);
      },
    },
    // simplify getting to the mint page in dev envs
    ...(envConfig.isDevelopment && entity.onchainId
      ? [
          {
            label: "Mint page",
            icon: <Link01 />,
            onClick: () => {
              window.open(
                getMintPageUrl({ ...entity, onchainId: entity.onchainId! })
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <Menu
        position="bottom-end"
        width={200}
        styles={{ itemIcon: { width: 16, height: 16 } }}
      >
        <Menu.Target>
          <UnstyledButton>{menuButton}</UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown w={260}>
          {items.map((item) => {
            const { label, ...rest } = item;
            return (
              <Menu.Item key={`${entity.id}-${label}`} {...rest}>
                {label}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
      {archiveCollectionModalOpened && (
        <CollectionArchiveModal
          isOpened
          entity={entity}
          onClose={() => setArchiveCollectionModalOpened(false)}
        />
      )}
      {duplicateCollectionModalOpened && (
        <CollectionDuplicateModal
          isOpened
          entity={entity}
          onClose={() => setDuplicateCollectionModalOpened(false)}
        />
      )}
    </>
  );
};

export default memo(CollectionMenu);
